<template>
  <a-drawer
    :open="purchase.open"
    title="购买积分"
    :placement="compact ? 'bottom' : 'right'"
    :height="compact ? '85%' : undefined"
    :class="{ 'account-settings-overlay': compact }"
    :width="drawerWidth"
    @close="purchase.close"
  >
    <div class="purchase-intro">
      <span class="purchase-intro__icon"><i class="ri-coin-line" aria-hidden="true" /></span>
      <div>
        <h3>为下一次创作补充积分</h3>
        <p>充值到当前账号，支付成功后余额自动更新。</p>
      </div>
    </div>
    <div class="purchase-balance">
      <span>当前可用积分</span
      ><strong
        >{{ credit.account?.availableCredits.toLocaleString() ?? '—' }}
        <small>Credits</small></strong
      >
    </div>
    <CouponRedemptionGuide :disabled="submitting" @redeem="purchase.close()" />
    <a-alert
      v-if="!loading && !channels.length"
      type="warning"
      show-icon
      message="暂未配置可用支付渠道"
      description="请联系平台管理员开通微信支付或支付宝。"
    />
    <div class="pack-heading">
      <h3>选择积分包</h3>
      <span>有效期从到账日起计算</span>
    </div>
    <a-spin :spinning="loading"
      ><div class="pack-list">
        <button
          v-for="item in packs"
          :key="item.id"
          class="pack"
          :class="{ active: selected?.id === item.id }"
          :aria-pressed="selected?.id === item.id"
          :disabled="loading || submitting"
          type="button"
          @click="selected = item"
        >
          <span class="pack-icon"
            ><i
              :class="
                selected?.id === item.id
                  ? 'ri-checkbox-circle-fill'
                  : 'ri-checkbox-blank-circle-line'
              "
              aria-hidden="true" /></span
          ><span
            ><strong>{{ item.name }}</strong
            ><small
              >{{ (item.grant.credits ?? 0).toLocaleString() }} Credits ·
              {{ item.grant.creditsExpiryDays ?? 365 }} 天有效</small
            ></span
          ><b>¥ {{ (item.amountMinor / 100).toFixed(2) }}</b></button
        ><a-empty v-if="!loading && !packs.length" description="暂未上架积分包" /></div
    ></a-spin>
    <template #footer>
      <div v-if="selected" class="purchase-total">
        <span>{{ selected.name }}</span
        ><strong>¥ {{ (selected.amountMinor / 100).toFixed(2) }}</strong>
      </div>
      <div class="purchase-actions">
        <a-button @click="goOrders"><i class="ri-file-list-3-line" />查看订单</a-button
        ><a-button
          type="primary"
          :loading="submitting"
          :disabled="loading || !selected || !channels.length"
          @click="createAndPay"
          >去支付</a-button
        >
      </div>
    </template>
  </a-drawer>
  <AccountSettingsModal
    v-model:open="quoteOpen"
    title="确认购买"
    :width="760"
    ok-text="提交订单"
    :confirm-loading="submitting"
    @ok="confirmOrder"
  >
    <OrderQuoteSummary
      v-if="quote"
      v-model:coupon-id="selectedCouponId"
      :quote="quote"
      :disabled="submitting"
      @change="refreshQuote"
      @redeem="
        quoteOpen = false;
        purchase.close();
      "
    />
  </AccountSettingsModal>
  <AccountSettingsModal v-model:open="paymentOpen" title="选择支付方式" :footer="null"
    ><p class="drawer-note">
      订单 {{ order?.orderNo }} · ¥ {{ order ? (order.amountMinor / 100).toFixed(2) : '0.00' }}
    </p>
    <a-space direction="vertical" style="width: 100%"
      ><a-button
        v-for="option in channels"
        :key="option.channel"
        block
        size="large"
        @click="pay(option.channel)"
        ><i
          :class="
            option.channel === 'wechat_native'
              ? 'ri-wechat-pay-line'
              : option.channel === 'alipay_page'
                ? 'ri-alipay-line'
                : 'ri-bank-card-line'
          "
        />{{ option.label }}</a-button
      ></a-space
    ></AccountSettingsModal
  >
  <AccountSettingsModal
    v-model:open="qrOpen"
    title="请使用微信扫码支付"
    :footer="null"
    @cancel="watchPaid"
    ><a-qrcode v-if="checkout?.type === 'qr_code'" :value="checkout.value" :size="220" />
    <p class="drawer-note">
      支付完成后将自动确认；如未更新，可在订单页继续支付。
    </p></AccountSettingsModal
  >
</template>

<script setup lang="ts">
import { openExternal } from '@/services/mobile-client';
import { useClientResume } from '@/composables/useClientResume';
import { message } from 'ant-design-vue';
import { toAppError } from '@/api/client';
import AccountSettingsModal from '@/components/settings/AccountSettingsModal.vue';
import OrderQuoteSummary from '@/components/commerce/OrderQuoteSummary.vue';
import CouponRedemptionGuide from '@/components/commerce/CouponRedemptionGuide.vue';
import { useCompactViewport } from '@/composables/useCompactViewport';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type {
  CheckoutAction,
  CommerceProductView,
  OrderView,
  OrderQuoteView,
  PaymentChannel,
} from 'shared';
import { commerceApi } from '@/api/commerce.api';
import { useCreditStore } from '@/stores/credit.store';
import { useCreditPurchaseStore } from '@/stores/credit-purchase.store';

const purchase = useCreditPurchaseStore();
const compact = useCompactViewport();
const credit = useCreditStore();
const router = useRouter();
const packs = ref<CommerceProductView[]>([]);
const selected = ref<CommerceProductView | null>(null);
const channels = ref<Array<{ channel: PaymentChannel; label: string }>>([]);
const loading = ref(false);
const submitting = ref(false);
const order = ref<OrderView | null>(null);
const paymentOpen = ref(false);
const qrOpen = ref(false);
const checkout = ref<CheckoutAction | null>(null);
const quoteOpen = ref(false);
const quote = ref<OrderQuoteView | null>(null);
const selectedCouponId = ref<string | null>(null);
let polling: number | undefined;
const drawerWidth = computed(() =>
  Math.min(440, typeof window === 'undefined' ? 440 : window.innerWidth - 20),
);
watch(
  () => purchase.open,
  (open) => {
    if (open) void load();
    else stopPolling();
  },
);
async function load(): Promise<void> {
  loading.value = true;
  try {
    const [products, config] = await Promise.all([
      commerceApi.products(),
      commerceApi.checkoutConfig(),
      credit.refreshAccount(),
    ]);
    packs.value = products.filter((item) => item.kind === 'credit_pack');
    selected.value =
      packs.value.find((item) => item.id === purchase.productId) ?? packs.value[0] ?? null;
    channels.value = config.channelOptions;
  } finally {
    loading.value = false;
  }
}
async function createAndPay(): Promise<void> {
  if (!selected.value) return;
  submitting.value = true;
  try {
    quote.value = await commerceApi.quoteOrder({
      sourceType: 'product',
      sourceId: selected.value.id,
    });
    selectedCouponId.value = quote.value.recommendedCouponId;
    quoteOpen.value = true;
  } finally {
    submitting.value = false;
  }
}
async function refreshQuote(): Promise<void> {
  if (!selected.value) return;
  quote.value = await commerceApi.quoteOrder({
    sourceType: 'product',
    sourceId: selected.value.id,
    couponId: selectedCouponId.value ?? null,
  });
}
async function confirmOrder(): Promise<void> {
  if (!selected.value) return;
  submitting.value = true;
  try {
    order.value = await commerceApi.createOrder({
      sourceType: 'product',
      sourceId: selected.value.id,
      couponId: selectedCouponId.value ?? null,
    });
    quoteOpen.value = false;
    if (order.value.status === 'paid') {
      await credit.refresh(1);
      await purchase.complete();
      return;
    }
    paymentOpen.value = true;
  } finally {
    submitting.value = false;
  }
}
async function pay(channel: PaymentChannel): Promise<void> {
  if (!order.value) return;
  checkout.value = await commerceApi.pay(order.value.id, channel);
  if (checkout.value.type === 'redirect') {
    await openExternal(checkout.value.value, true);
    return;
  }
  paymentOpen.value = false;
  qrOpen.value = true;
  startPolling();
}
function startPolling(): void {
  stopPolling();
  polling = window.setInterval(() => void watchPaid(), 3000);
}
function stopPolling(): void {
  if (polling) window.clearInterval(polling);
  polling = undefined;
}
async function watchPaid(): Promise<void> {
  if (!order.value) return;
  const latest = await commerceApi.order(order.value.id);
  if (latest.status !== 'paid') return;
  stopPolling();
  paymentOpen.value = false;
  qrOpen.value = false;
  await credit.refresh(1);
  await purchase.complete();
}
useClientResume(() => {
  if (purchase.open && order.value) void watchPaid().catch(cause => message.error(toAppError(cause).message));
});
function goOrders(): void {
  purchase.close();
  void router.push({ name: 'account-orders', query: { tab: 'orders' } });
}
onBeforeUnmount(stopPolling);
</script>

<style scoped src="./CreditPurchaseDrawer.css"></style>
